/**
 * Device information utilities for session tracking
 */

export interface DeviceInfo {
  browser: string
  browserVersion: string
  os: string
  osVersion: string
  device: string
  deviceType: 'desktop' | 'mobile' | 'tablet'
  screenResolution: string
  language: string
  timezone: string
  ip?: string
  city?: string
  country?: string
  countryCode?: string
}

/**
 * Parse user agent string to extract browser, OS, and device info
 */
function parseUserAgent(ua: string): Partial<DeviceInfo> {
  const result: Partial<DeviceInfo> = {
    browser: 'Unknown',
    browserVersion: '',
    os: 'Unknown',
    osVersion: '',
    device: 'Unknown',
    deviceType: 'desktop',
  }

  // Detect browser
  if (ua.includes('Firefox/')) {
    result.browser = 'Firefox'
    result.browserVersion = ua.match(/Firefox\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Edg/')) {
    result.browser = 'Edge'
    result.browserVersion = ua.match(/Edg\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Chrome/')) {
    result.browser = 'Chrome'
    result.browserVersion = ua.match(/Chrome\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Safari/') && !ua.includes('Chrome')) {
    result.browser = 'Safari'
    result.browserVersion = ua.match(/Version\/([\d.]+)/)?.[1] || ''
  } else if (ua.includes('Opera') || ua.includes('OPR/')) {
    result.browser = 'Opera'
    result.browserVersion = ua.match(/(?:Opera|OPR)\/([\d.]+)/)?.[1] || ''
  }

  // Detect OS
  if (ua.includes('Windows NT 10')) {
    result.os = 'Windows'
    result.osVersion = '10/11'
  } else if (ua.includes('Windows NT 6.3')) {
    result.os = 'Windows'
    result.osVersion = '8.1'
  } else if (ua.includes('Windows NT 6.2')) {
    result.os = 'Windows'
    result.osVersion = '8'
  } else if (ua.includes('Windows NT 6.1')) {
    result.os = 'Windows'
    result.osVersion = '7'
  } else if (ua.includes('Mac OS X')) {
    result.os = 'macOS'
    result.osVersion = ua.match(/Mac OS X ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''
  } else if (ua.includes('Android')) {
    result.os = 'Android'
    result.osVersion = ua.match(/Android ([\d.]+)/)?.[1] || ''
  } else if (ua.includes('iPhone OS') || ua.includes('iPad')) {
    result.os = 'iOS'
    result.osVersion = ua.match(/OS ([\d_]+)/)?.[1]?.replace(/_/g, '.') || ''
  } else if (ua.includes('Linux')) {
    result.os = 'Linux'
    result.osVersion = ''
  }

  // Detect device type
  if (ua.includes('Mobile') || ua.includes('Android') && !ua.includes('Tablet')) {
    result.deviceType = 'mobile'
    if (ua.includes('iPhone')) {
      result.device = 'iPhone'
    } else if (ua.includes('Android')) {
      // Try to extract Android device model
      const match = ua.match(/Android[\d.]+;\s*([^)]+)/)?.[1]
      result.device = match?.split(';')[0]?.trim() || 'Android Phone'
    } else {
      result.device = 'Mobile'
    }
  } else if (ua.includes('iPad') || (ua.includes('Android') && ua.includes('Tablet'))) {
    result.deviceType = 'tablet'
    result.device = ua.includes('iPad') ? 'iPad' : 'Android Tablet'
  } else {
    result.deviceType = 'desktop'
    if (ua.includes('Macintosh')) {
      result.device = 'Mac'
    } else if (ua.includes('Windows')) {
      result.device = 'Windows PC'
    } else if (ua.includes('Linux')) {
      result.device = 'Linux PC'
    } else {
      result.device = 'Desktop'
    }
  }

  return result
}

/**
 * Get IP and location info - uses our API route first (most reliable)
 * then falls back to third-party services
 */
async function getIPInfo(): Promise<Partial<DeviceInfo>> {
  if (typeof window === 'undefined') {
    return {}
  }

  // Try our own API route first (server-side, no CORS issues)
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)

    const response = await fetch('/api/ip-info', {
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (response.ok) {
      const data = await response.json()
      if (data.ip) {
        return {
          ip: data.ip,
          city: data.city,
          country: data.country,
          countryCode: data.countryCode,
        }
      }
    }
  } catch {
    // Try fallback
  }

  // Fallback to ipinfo.io (client-side, may be blocked)
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 3000)

    const response = await fetch('https://ipinfo.io/json', {
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (response.ok) {
      const data = await response.json()
      return {
        ip: data.ip,
        city: data.city,
        country: data.country,
        countryCode: data.country,
      }
    }
  } catch {
    // All failed
  }

  return {}
}

/**
 * Collect all available device information
 */
export async function collectDeviceInfo(): Promise<DeviceInfo> {
  const ua = typeof navigator !== 'undefined' ? navigator.userAgent : ''
  const parsed = parseUserAgent(ua)

  // Get screen info
  const screenResolution = typeof window !== 'undefined'
    ? `${window.screen.width}x${window.screen.height}`
    : 'Unknown'

  // Get language and timezone
  const language = typeof navigator !== 'undefined'
    ? navigator.language
    : 'Unknown'

  const timezone = typeof Intl !== 'undefined'
    ? Intl.DateTimeFormat().resolvedOptions().timeZone
    : 'Unknown'

  // Get IP info (async)
  const ipInfo = await getIPInfo()

  return {
    browser: parsed.browser || 'Unknown',
    browserVersion: parsed.browserVersion || '',
    os: parsed.os || 'Unknown',
    osVersion: parsed.osVersion || '',
    device: parsed.device || 'Unknown',
    deviceType: parsed.deviceType || 'desktop',
    screenResolution,
    language,
    timezone,
    ...ipInfo,
  }
}

/**
 * Get a short display string for the device
 */
export function getDeviceDisplayName(info: Partial<DeviceInfo>): string {
  const parts: string[] = []

  if (info.browser) {
    parts.push(info.browser)
  }

  if (info.os) {
    parts.push(`on ${info.os}`)
  }

  return parts.join(' ') || 'Unknown Device'
}

/**
 * Get location display string
 */
export function getLocationDisplay(info: Partial<DeviceInfo>): string {
  if (info.city && info.country) {
    return `${info.city}, ${info.country}`
  }
  if (info.country) {
    return info.country
  }
  return ''
}
